import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ElementRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';

@Component({
  selector: 'viewer-fullscreen-button',
  templateUrl: './viewer-fullscreen-button.component.html',
  styleUrl: './viewer-fullscreen-button.component.scss',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ViewerFullscreenButtonComponent implements OnChanges, OnDestroy {
  @Input() active: boolean = false;
  @Output() clicked: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('fullscreenButton') fullscreenButton!: ElementRef<HTMLButtonElement>;
  private tooltipEl: HTMLDivElement | null = null;
  /** True while pointer is over the button (mouseover/mouseout). */
  private isOverButton: boolean = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['active'] && !changes['active'].firstChange) {
      const prev: boolean = changes['active'].previousValue as boolean;
      const next: boolean = changes['active'].currentValue as boolean;
      if (prev !== next) {
        this.hideTooltip();
      }
    }
  }

  public showTooltip(): void {
    const button: HTMLButtonElement | undefined = this.fullscreenButton?.nativeElement;
    if (!button) {
      return;
    }

    const tooltipText: string = this.active ? 'Exit Full Screen' : 'Full Screen';

    if (!this.tooltipEl) {
      this.tooltipEl = document.createElement('div');
      this.tooltipEl.className = 'viewer-fullscreen-tooltip';
      this.tooltipEl.style.position = 'fixed';
      this.tooltipEl.style.pointerEvents = 'none';
      this.tooltipEl.style.zIndex = '20000';
      this.tooltipEl.style.visibility = 'hidden';
      document.body.appendChild(this.tooltipEl);
    }

    this.tooltipEl.textContent = tooltipText;
    this.tooltipEl.style.visibility = 'visible';
    this.positionTooltip();
  }

  public hideTooltip(): void {
    if (!this.tooltipEl) {
      return;
    }
    this.tooltipEl.style.visibility = 'hidden';
  }

  private positionTooltip(): void {
    if (!this.tooltipEl) {
      return;
    }

    const buttonRect: DOMRect = this.fullscreenButton.nativeElement.getBoundingClientRect();

    this.tooltipEl.style.left = `${buttonRect.left + buttonRect.width / 2}px`;
    const tooltipHeight: number = this.tooltipEl.getBoundingClientRect().height || 24;
    this.tooltipEl.style.top = `${buttonRect.top - 10 - tooltipHeight}px`;
    this.tooltipEl.style.transform = 'translateX(calc(-100% + 14px))';
  }

  ngOnDestroy(): void {
    if (this.tooltipEl) {
      this.tooltipEl.remove();
      this.tooltipEl = null;
    }
  }

  public onPointerEnter(): void {
    this.isOverButton = true;
    this.showTooltip();
  }

  public onPointerLeave(): void {
    this.isOverButton = false;
    this.hideTooltip();
  }

  public onClick(): void {
    // Fullscreen toggle moves the layout; mouseout may not run, so clear hover state and never re-show
    // until the user enters the button again (new mouseover).
    this.isOverButton = false;
    this.hideTooltip();

    this.clicked.emit();

    requestAnimationFrame(() => {
      this.hideTooltip();
      requestAnimationFrame(() => {
        this.hideTooltip();
      });
    });
  }
}
